/**
 * @file: MediaRenderer.tsx
 *
 * A single image or video (`media` payload). Full form renders an
 * aspect-fit thumbnail (with a play overlay + duration for video); `quoted`
 * renders a small square thumbnail for a reply preview.
 */

import { Typography } from '@euler/app/components/typography';
import { AdvancedImage } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMessagePayloadMedia } from '@euler/model/chat';
import { Play } from 'lucide-react-native';
import { FC, memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { formatDuration, mediaBox, stripHash } from './mediaLayout';
import { PayloadRendererProps } from './types';

interface Props extends PayloadRendererProps<ChatMessagePayloadMedia> {
  onPreviewImage?: (url: string) => void;
  onPreviewVideo?: (url: string) => void;
}

export const MediaRenderer: FC<Props> = memo(
  ({ payload, quoted, onPreviewImage, onPreviewVideo }) => {
    const { rpx } = useResponsiveContext();
    const url = stripHash(payload.url);
    const isVideo = payload.kind === 'video';

    const handlePress = useCallback(() => {
      if (isVideo) onPreviewVideo?.(url);
      else onPreviewImage?.(url);
    }, [isVideo, url, onPreviewVideo, onPreviewImage]);

    if (quoted) {
      const side = rpx(40);
      return (
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={handlePress}
          style={{
            borderRadius: rpx(6),
            overflow: 'hidden',
            width: side,
            height: side,
          }}
        >
          <AdvancedImage
            uri={payload.coverUrl ?? payload.url}
            style={{ width: side, height: side }}
          />
          {isVideo ? (
            <View
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Play color="#fff" size={rpx(16)} fill="#fff" />
            </View>
          ) : null}
        </TouchableOpacity>
      );
    }

    const box = mediaBox(payload.width, payload.height, rpx);

    if (isVideo) {
      return (
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={handlePress}
          style={{ borderRadius: rpx(10), overflow: 'hidden', ...box }}
        >
          <AdvancedImage
            uri={payload.coverUrl ?? payload.url}
            style={{ ...box }}
          />
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <View
              style={{
                width: rpx(44),
                height: rpx(44),
                borderRadius: rpx(22),
                backgroundColor: 'rgba(0,0,0,0.45)',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Play color="#fff" size={rpx(22)} fill="#fff" />
            </View>
          </View>
          {payload.durationMs ? (
            <View
              style={{
                position: 'absolute',
                right: rpx(6),
                bottom: rpx(6),
                backgroundColor: 'rgba(0,0,0,0.55)',
                borderRadius: rpx(4),
                paddingHorizontal: rpx(6),
                paddingVertical: rpx(1),
              }}
            >
              <Typography.Text size={rpx(10)} color="#fff">
                {formatDuration(payload.durationMs)}
              </Typography.Text>
            </View>
          ) : null}
        </TouchableOpacity>
      );
    }

    return (
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={handlePress}
        style={{ borderRadius: rpx(10), overflow: 'hidden', ...box }}
      >
        {/* Render the ~600px cover, not the full-res original — decoding a
            full-resolution JPEG into a small bubble is the chat list's main
            source of scroll jank. `coverUrl` carries the same blurhash `#`
            fragment (placeholder preserved); the press handler opens the
            full-res `url` in the previewer. Falls back to `url` for older
            messages with no cover. */}
        <AdvancedImage
          uri={payload.coverUrl ?? payload.url}
          style={{ ...box }}
        />
      </TouchableOpacity>
    );
  },
);
